/**
 * Upsell App - Core JavaScript
 * Handles upsell offer display, cart interactions, and discount application
 *
 * Offers are bidirectional: either product group can trigger the offer for the other.
 * Supports multiple products per group - shows as cards when multiple options.
 * The discount applies to whichever product is added second.
 */

(function () {
  'use strict';

  const UpsellApp = {
    offers: [],
    customerData: {},
    shownOffers: new Set(), // Track shown offers by offer ID
    offerQueue: [],
    currentOffer: null,
    currentOfferData: null,
    isProcessing: false,
    isInitialized: false,
    pendingOfferCheck: false,

    /**
     * Initialize the upsell app
     */
    init() {
      if (this.isInitialized) return;

      this.loadData();

      // Don't initialize for B2B customers
      if (this.customerData.isB2B) {
        console.log('[Upsell] B2B customer detected, offers disabled');
        return;
      }

      if (this.offers.length === 0) {
        console.log('[Upsell] No active offers found');
        return;
      }

      this.bindEvents();
      this.bindPopupEvents();
      this.isInitialized = true;
      console.log('[Upsell] Initialized with', this.offers.length, 'offers');
    },

    /**
     * Load offer and customer data from embedded JSON
     */
    loadData() {
      try {
        const offerDataEl = document.getElementById('upsell-offer-data');
        const customerDataEl = document.getElementById('upsell-customer-data');

        if (offerDataEl) {
          const data = JSON.parse(offerDataEl.textContent);
          this.offers = data.offers.sort((a, b) => a.priority - b.priority);
        }

        if (customerDataEl) {
          this.customerData = JSON.parse(customerDataEl.textContent);
        }
      } catch (e) {
        console.error('[Upsell] Error loading data:', e);
      }
    },

    /**
     * Bind cart add event listeners
     */
    bindEvents() {
      const self = this;

      // Intercept all cart/add requests
      this.interceptCartAdd();

      // Listen for Horizon's cart:update event
      document.addEventListener('cart:update', (e) => {
        if (!this.isProcessing) {
          this.pendingOfferCheck = true;
          console.log('[Upsell] Cart updated, will check offers when drawer closes');
        }
      });

      // Listen for cart drawer close event directly on the cart drawer element
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer) {
        cartDrawer.addEventListener('dialog:close', () => {
          if (this.pendingOfferCheck) {
            this.pendingOfferCheck = false;
            console.log('[Upsell] Cart drawer closed, checking for offers');
            this.checkForOffers();
          }
        });
        console.log('[Upsell] Listening for cart drawer close');
      } else {
        customElements.whenDefined('cart-drawer-component').then(() => {
          const drawer = document.querySelector('cart-drawer-component');
          if (drawer) {
            drawer.addEventListener('dialog:close', () => {
              if (self.pendingOfferCheck) {
                self.pendingOfferCheck = false;
                console.log('[Upsell] Cart drawer closed, checking for offers');
                self.checkForOffers();
              }
            });
            console.log('[Upsell] Listening for cart drawer close (deferred)');
          }
        });
      }

      // Fallback events
      const cartEvents = ['cart:item-added', 'cart:add', 'ajaxProduct:added', 'product:added', 'cart:item:added'];
      cartEvents.forEach((eventName) => {
        document.addEventListener(eventName, (e) => {
          if (!this.isProcessing) {
            this.pendingOfferCheck = true;
          }
        });
      });
    },

    /**
     * Intercept fetch and XHR requests to cart/add endpoint
     */
    interceptCartAdd() {
      const self = this;
      const originalFetch = window.fetch;

      window.fetch = async function (...args) {
        const response = await originalFetch.apply(this, args);

        const url = args[0];
        if (typeof url === 'string' && url.includes('/cart/add') && !self.isProcessing) {
          self.pendingOfferCheck = true;
          console.log('[Upsell] Detected cart add, will check offers when drawer closes');
        }

        return response;
      };

      const originalXHROpen = XMLHttpRequest.prototype.open;
      const originalXHRSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        this._upsellUrl = url;
        return originalXHROpen.apply(this, [method, url, ...rest]);
      };

      XMLHttpRequest.prototype.send = function (body) {
        const xhr = this;
        if (this._upsellUrl && this._upsellUrl.includes('/cart/add')) {
          this.addEventListener('load', function () {
            if (!self.isProcessing) {
              self.pendingOfferCheck = true;
            }
          });
        }
        return originalXHRSend.apply(this, [body]);
      };
    },

    /**
     * Check for matching offers and show popup
     */
    async checkForOffers() {
      if (this.isProcessing) return;

      const cart = await this.getCart();
      const matchingOffers = this.findMatchingOffers(cart);

      if (matchingOffers.length === 0) {
        console.log('[Upsell] No matching offers found');
        return;
      }

      // Filter out already shown offers
      const newOffers = matchingOffers.filter((offerData) => {
        return !this.shownOffers.has(offerData.offer.id);
      });

      if (newOffers.length === 0) {
        console.log('[Upsell] All matching offers already shown');
        return;
      }

      console.log('[Upsell] Found', newOffers.length, 'new offers to show');
      this.offerQueue = [...this.offerQueue, ...newOffers];
      this.showNextOffer();
    },

    /**
     * Find offers that match products in cart (bidirectional)
     * Supports product lists - if ANY product from group A is in cart, offer group B
     * @param {Object} cart - The current cart object
     * @returns {Array} Matching offers with productsToOffer
     */
    findMatchingOffers(cart) {
      const cartProductIds = cart.items.map((item) => item.product_id);
      const matchingOffers = [];

      for (const offer of this.offers) {
        const productsA = offer.productsA || [];
        const productsB = offer.productsB || [];

        // Get IDs for both groups
        const productAIds = productsA.map((p) => this.extractProductId(p.id));
        const productBIds = productsB.map((p) => this.extractProductId(p.id));

        // Check which products from each group are in cart
        const productsAInCart = productsA.filter((p) => cartProductIds.includes(this.extractProductId(p.id)));
        const productsBInCart = productsB.filter((p) => cartProductIds.includes(this.extractProductId(p.id)));

        const anyAInCart = productsAInCart.length > 0;
        const anyBInCart = productsBInCart.length > 0;

        // Filter out products already in cart from the offer list
        const productsANotInCart = productsA.filter((p) => !cartProductIds.includes(this.extractProductId(p.id)));
        const productsBNotInCart = productsB.filter((p) => !cartProductIds.includes(this.extractProductId(p.id)));

        if (anyAInCart && productsBNotInCart.length > 0) {
          // Product(s) from A in cart, offer product(s) from B that aren't in cart
          matchingOffers.push({
            offer: offer,
            productsInCart: productsAInCart,
            productsToOffer: productsBNotInCart,
            headline: offer.headlineA,
            bodyText: offer.bodyTextA
          });
        } else if (anyBInCart && productsANotInCart.length > 0) {
          // Product(s) from B in cart, offer product(s) from A that aren't in cart
          matchingOffers.push({
            offer: offer,
            productsInCart: productsBInCart,
            productsToOffer: productsANotInCart,
            headline: offer.headlineB,
            bodyText: offer.bodyTextB
          });
        }
      }

      return matchingOffers;
    },

    /**
     * Extract numeric product ID from Shopify GID or number
     */
    extractProductId(gidOrId) {
      if (typeof gidOrId === 'number') return gidOrId;
      if (typeof gidOrId === 'string' && gidOrId.includes('gid://')) {
        return parseInt(gidOrId.split('/').pop(), 10);
      }
      return parseInt(gidOrId, 10);
    },

    /**
     * Fetch current cart data
     */
    async getCart() {
      const response = await fetch('/cart.js');
      return response.json();
    },

    /**
     * Show the next offer in the queue
     */
    showNextOffer() {
      if (this.offerQueue.length === 0) return;

      const offerData = this.offerQueue.shift();
      this.currentOffer = offerData.offer;
      this.currentOfferData = offerData;
      this.shownOffers.add(offerData.offer.id);

      this.renderOffer(offerData);
      this.openPopup();
      this.trackImpression(offerData);
    },

    /**
     * Render offer data into the popup
     * Shows cards for multiple products, single product view for one product
     */
    renderOffer(offerData) {
      const popup = document.getElementById('upsell-popup');
      const container = popup.querySelector('.upsell-popup__container');
      const offer = offerData.offer;
      const productsToOffer = offerData.productsToOffer;

      // Set headline and body
      popup.querySelector('#upsell-headline').textContent = offerData.headline || '';
      popup.querySelector('#upsell-body').textContent = offerData.bodyText || '';

      const productsGrid = popup.querySelector('#upsell-products-grid');
      const singleProductView = popup.querySelector('#upsell-single-product');

      // Clear previous products
      productsGrid.innerHTML = '';

      if (productsToOffer.length === 1) {
        // Single product - use single product view
        container.classList.remove('has-multiple-products');
        productsGrid.style.display = 'none';
        singleProductView.style.display = 'block';
        this.renderSingleProduct(productsToOffer[0], offer, popup);
      } else {
        // Multiple products - use card grid
        container.classList.add('has-multiple-products');
        productsGrid.style.display = 'grid';
        singleProductView.style.display = 'none';
        this.renderProductCards(productsToOffer, offer, productsGrid);
      }
    },

    /**
     * Render single product view
     */
    renderSingleProduct(product, offer, popup) {
      const img = popup.querySelector('#upsell-product-image');
      img.src = product.image || '';
      img.alt = product.title || '';

      popup.querySelector('#upsell-product-title').textContent = product.title || '';

      const originalPrice = (product.price || 0) / 100;
      const discountedPrice = this.calculateDiscountedPrice(originalPrice, offer);

      popup.querySelector('#upsell-original-price').textContent = this.formatMoney(originalPrice);
      popup.querySelector('#upsell-offer-price').textContent = this.formatMoney(discountedPrice);

      // Variant selector
      this.renderVariantSelector(product, offer, popup);

      // Button
      const addBtn = popup.querySelector('#upsell-add-btn');
      addBtn.textContent = offer.buttonText || 'Add to Cart';
      addBtn.dataset.offerId = offer.id;
      addBtn.dataset.offerHandle = offer.handle;
      addBtn.dataset.productHandle = product.handle;
      addBtn.dataset.variantId = this.extractProductId(product.defaultVariantId);
      addBtn.disabled = false;
      addBtn.classList.remove('upsell-popup__add-btn--loading');
    },

    /**
     * Render product cards for multiple products
     */
    renderProductCards(products, offer, container) {
      products.forEach((product) => {
        const card = document.createElement('div');
        card.className = 'upsell-popup__product-card';

        const originalPrice = (product.price || 0) / 100;
        const discountedPrice = this.calculateDiscountedPrice(originalPrice, offer);

        card.innerHTML = `
          <img
            src="${product.image || ''}"
            alt="${product.title || ''}"
            class="upsell-popup__card-image"
            width="120"
            height="120"
            loading="lazy"
          >
          <h3 class="upsell-popup__card-title">${product.title || ''}</h3>
          <div class="upsell-popup__card-pricing">
            <span class="upsell-popup__card-original-price">${this.formatMoney(originalPrice)}</span>
            <span class="upsell-popup__card-offer-price">${this.formatMoney(discountedPrice)}</span>
          </div>
          <button
            class="upsell-popup__card-add-btn"
            data-offer-id="${offer.id}"
            data-offer-handle="${offer.handle}"
            data-product-handle="${product.handle}"
            data-variant-id="${this.extractProductId(product.defaultVariantId)}"
          >
            ${offer.buttonText || 'Add'}
          </button>
        `;

        // Bind click handler to the card's add button
        const addBtn = card.querySelector('.upsell-popup__card-add-btn');
        addBtn.addEventListener('click', (e) => this.handleCardAddClick(e, product, offer));

        container.appendChild(card);
      });
    },

    /**
     * Handle add button click on a product card
     */
    async handleCardAddClick(event, product, offer) {
      const btn = event.currentTarget;
      const variantId = btn.dataset.variantId;
      const productHandle = btn.dataset.productHandle;

      if (!variantId) {
        console.error('[Upsell] No variant selected');
        return;
      }

      btn.disabled = true;
      btn.classList.add('upsell-popup__card-add-btn--loading');
      btn.textContent = 'Adding...';
      this.isProcessing = true;

      try {
        await this.addOfferToCart(variantId, productHandle, offer);
        this.trackAcceptance(offer, product);
        this.closePopup();
        this.refreshAndOpenCart();
      } catch (e) {
        console.error('[Upsell] Error adding to cart:', e);
        btn.textContent = 'Error';
        btn.disabled = false;
        btn.classList.remove('upsell-popup__card-add-btn--loading');
      } finally {
        this.isProcessing = false;
      }
    },

    /**
     * Calculate discounted price based on offer settings
     */
    calculateDiscountedPrice(originalPrice, offer) {
      let discountedPrice;

      if (offer.discountType === 'percentage') {
        discountedPrice = originalPrice * (1 - (offer.discountValue || 0) / 100);
      } else {
        discountedPrice = originalPrice - (offer.discountValue || 0);
      }

      return Math.max(0, discountedPrice);
    },

    /**
     * Render variant selector if product has multiple variants
     */
    renderVariantSelector(product, offer, popup) {
      const selectorContainer = popup.querySelector('#upsell-variant-selector');
      const variants = product.variants || [];

      selectorContainer.innerHTML = '';

      if (variants.length <= 1) {
        selectorContainer.style.display = 'none';
        selectorContainer.dataset.selectedVariantId = this.extractProductId(
          product.defaultVariantId || (variants[0] && variants[0].id)
        );
        return;
      }

      const availableVariants = variants.filter((v) => v.available);

      if (availableVariants.length === 0) {
        selectorContainer.style.display = 'none';
        return;
      }

      selectorContainer.style.display = 'block';

      const select = document.createElement('select');
      select.id = 'upsell-variant-select';
      select.className = 'upsell-popup__variant-select';

      availableVariants.forEach((variant, index) => {
        const option = document.createElement('option');
        option.value = this.extractProductId(variant.id);
        option.textContent = variant.title;
        if (index === 0) option.selected = true;
        select.appendChild(option);
      });

      select.addEventListener('change', (e) => {
        const selectedVariant = variants.find(
          (v) => this.extractProductId(v.id) === parseInt(e.target.value, 10)
        );
        if (selectedVariant) {
          this.updatePriceDisplay(selectedVariant.price / 100, offer);
        }
        selectorContainer.dataset.selectedVariantId = e.target.value;

        // Also update the add button's variant ID
        const addBtn = popup.querySelector('#upsell-add-btn');
        if (addBtn) {
          addBtn.dataset.variantId = e.target.value;
        }
      });

      selectorContainer.appendChild(select);
      selectorContainer.dataset.selectedVariantId = select.value;
    },

    /**
     * Update price display when variant changes
     */
    updatePriceDisplay(originalPrice, offer) {
      const popup = document.getElementById('upsell-popup');
      const discountedPrice = this.calculateDiscountedPrice(originalPrice, offer);

      popup.querySelector('#upsell-original-price').textContent = this.formatMoney(originalPrice);
      popup.querySelector('#upsell-offer-price').textContent = this.formatMoney(discountedPrice);
    },

    /**
     * Format price as currency string
     */
    formatMoney(amount) {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(amount);
    },

    /**
     * Open the popup modal
     */
    openPopup() {
      const popup = document.getElementById('upsell-popup');
      popup.setAttribute('aria-hidden', 'false');
      popup.classList.add('is-open');
      document.body.classList.add('upsell-popup-open');

      const firstFocusable = popup.querySelector('button, [href], input, select');
      if (firstFocusable) firstFocusable.focus();
    },

    /**
     * Close the popup modal
     */
    closePopup() {
      const popup = document.getElementById('upsell-popup');
      popup.setAttribute('aria-hidden', 'true');
      popup.classList.remove('is-open');
      document.body.classList.remove('upsell-popup-open');

      setTimeout(() => this.showNextOffer(), 400);
    },

    /**
     * Bind popup UI event listeners
     */
    bindPopupEvents() {
      const popup = document.getElementById('upsell-popup');

      // Close buttons (track as decline)
      popup.querySelectorAll('[data-upsell-close]').forEach((el) => {
        el.addEventListener('click', () => {
          this.trackDecline(this.currentOfferData);
          this.closePopup();
        });
      });

      // Single product add button
      popup.querySelector('#upsell-add-btn').addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        const selectorContainer = popup.querySelector('#upsell-variant-selector');
        const variantId = btn.dataset.variantId || selectorContainer?.dataset.selectedVariantId;
        const productHandle = btn.dataset.productHandle;

        if (!variantId) {
          console.error('[Upsell] No variant selected');
          return;
        }

        btn.disabled = true;
        btn.classList.add('upsell-popup__add-btn--loading');
        btn.textContent = 'Adding...';
        this.isProcessing = true;

        try {
          await this.addOfferToCart(variantId, productHandle, this.currentOffer);

          // Get product from current offer data
          const product = this.currentOfferData?.productsToOffer?.[0];
          if (product) {
            this.trackAcceptance(this.currentOffer, product);
          }

          this.closePopup();
          this.refreshAndOpenCart();
        } catch (e) {
          console.error('[Upsell] Error adding to cart:', e);
          btn.textContent = 'Error - Try Again';
          btn.disabled = false;
          btn.classList.remove('upsell-popup__add-btn--loading');
        } finally {
          this.isProcessing = false;
        }
      });

      // Escape key (track as decline)
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && popup.classList.contains('is-open')) {
          this.trackDecline(this.currentOfferData);
          this.closePopup();
        }
      });
    },

    /**
     * Add offer product to cart with cart attribute for discount
     * Cart attribute format: "product-handle:discount-type:discount-value:offer-name:trigger-handle,..."
     * The trigger-handle is used to verify both products are still in cart
     */
    async addOfferToCart(variantId, productHandle, offer) {
      const cart = await this.getCart();
      const existingOffers = cart.attributes && cart.attributes.upsell_offers
        ? cart.attributes.upsell_offers.split(',').filter(Boolean)
        : [];

      // Get the trigger product handle from the current offer data
      const triggerProduct = this.currentOfferData?.productsInCart?.[0];
      const triggerHandle = triggerProduct?.handle || '';

      // Build offer entry with discount info for the function
      // Format: product-handle:discount-type:discount-value:offer-name:trigger-handle
      const discountType = offer.discountType || 'percentage';
      const discountValue = offer.discountValue || 0;
      const offerName = (offer.name || '').replace(/:/g, '-'); // Remove colons from name
      const offerEntry = productHandle + ':' + discountType + ':' + discountValue + ':' + offerName + ':' + triggerHandle;

      // Check if this product already has an offer entry
      const existingIndex = existingOffers.findIndex(e => e.startsWith(productHandle + ':'));
      if (existingIndex === -1) {
        existingOffers.push(offerEntry);
      }

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: 1
        })
      });

      if (!addResponse.ok) {
        throw new Error('Failed to add item to cart');
      }

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            upsell_offers: existingOffers.join(',')
          }
        })
      });
    },

    /**
     * Refresh cart and open drawer after adding upsell product
     */
    refreshAndOpenCart() {
      const event = new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          data: {
            source: 'upsell-app'
          }
        }
      });
      document.dispatchEvent(event);

      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        setTimeout(() => {
          cartDrawer.open();
        }, 200);
      }
    },

    /**
     * Track offer impression for analytics
     */
    trackImpression(offerData) {
      const eventData = {
        offer_id: offerData.offer.id,
        offer_name: offerData.offer.name,
        products_offered_count: offerData.productsToOffer.length
      };

      try {
        // Shopify Plus Customer Events
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish('upsell_impression', eventData);
        }
        // Legacy ShopifyAnalytics
        if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib && typeof window.ShopifyAnalytics.lib.track === 'function') {
          window.ShopifyAnalytics.lib.track('Upsell Impression', eventData);
        }
        console.log('[Upsell] Tracked impression:', eventData);
      } catch (e) {
        console.log('[Upsell] Tracking error:', e);
      }
    },

    /**
     * Track offer acceptance for analytics
     */
    trackAcceptance(offer, product) {
      const eventData = {
        offer_id: offer.id,
        offer_name: offer.name,
        product_added: product.handle,
        discount_value: offer.discountValue,
        discount_type: offer.discountType
      };

      try {
        // Shopify Plus Customer Events
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish('upsell_accepted', eventData);
        }
        // Legacy ShopifyAnalytics
        if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib && typeof window.ShopifyAnalytics.lib.track === 'function') {
          window.ShopifyAnalytics.lib.track('Upsell Accepted', eventData);
        }
        console.log('[Upsell] Tracked acceptance:', eventData);
      } catch (e) {
        console.log('[Upsell] Tracking error:', e);
      }
    },

    /**
     * Track offer decline for analytics
     */
    trackDecline(offerData) {
      if (!offerData) return;

      const eventData = {
        offer_id: offerData.offer.id,
        offer_name: offerData.offer.name,
        products_offered_count: offerData.productsToOffer.length
      };

      try {
        // Shopify Plus Customer Events
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish('upsell_declined', eventData);
        }
        // Legacy ShopifyAnalytics
        if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib && typeof window.ShopifyAnalytics.lib.track === 'function') {
          window.ShopifyAnalytics.lib.track('Upsell Declined', eventData);
        }
        console.log('[Upsell] Tracked decline:', eventData);
      } catch (e) {
        console.log('[Upsell] Tracking error:', e);
      }
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      UpsellApp.init();
    });
  } else {
    UpsellApp.init();
  }
})();
